import {
  CREATE_USER_REQUEST,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAIL,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_FAIL,
  LOGIN_USER_RESET,
  GET_OTP_REQUEST,
  GET_OTP_SUCCESS,
  GET_OTP_FAIL,
  VERIFY_OTP,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAIL,
  LOGOUT_USER_SUCCESS,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_FAIL,
  FETCH_HOSPITAL_PAYMENT_CONFIG_REQUEST,
  FETCH_HOSPITAL_PAYMENT_CONFIG_SUCCESS,
  FETCH_HOSPITAL_PAYMENT_CONFIG_FAIL,
  UPDATE_HOSPITAL_PAYMENT_CONFIG,
  FETCH_CONSULTATION_COSTS_REQUEST,
  FETCH_CONSULTATION_COSTS_SUCCESS,
  FETCH_CONSULTATION_COSTS_FAIL,
  UPDATE_CONSULTATION_COSTS
} from "../constants/userConstants.js";

import UserService from "../../services/userService.js";
import TokenService from "../../services/tokenService.js";
import fcmService from "../../services/fcmService.js";
import notificationApi from "../../services/notificationApi.js";
import { toast } from "../../utils/toast";
import { data } from "autoprefixer";


/**
 * Securely store user data to localStorage with token management
 * @param {Object} responseData - User data from API response
 */
const pushToLocalStorage = (responseData) => {
  try {
    const actualData = responseData.data || responseData;

    if (actualData.token) {
      if (actualData.expires_in) {
        TokenService.setToken(
          actualData.token,
          actualData.refresh_token,
          actualData.expires_in
        );
      } else {
        TokenService.setSanctumToken(actualData.token);
      }
    }

    const hospitalData = actualData.hospital || {};
    const storageItems = {
      userInfo: actualData.patient,
      hospital: hospitalData.hospital_id || actualData.hospital_id,
      hospital_name: hospitalData.system_name || actualData.hospital_name,
      hospital_phone: hospitalData.phone || actualData.hospital_phone,
      type: actualData.type,
      consultation_audio: hospitalData.audio_consultation || hospitalData.consultation_audio || 2000,
      consultation_video: hospitalData.video_consultation || hospitalData.consultation_video || 4000,
      hospital_primary_color: hospitalData.primary_color,
      hospital_secondary_color: hospitalData.secondary_color,
      hospital_opaque_color: hospitalData.opaque_color,
    };

    Object.entries(storageItems).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, JSON.stringify(value));
      }
    });
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    toast.error('Failed to save user data locally');
  }
};

/**
 * Securely clear user data from localStorage and token storage
 */
const removeFromLocalStorage = () => {
  try {
    // Clear tokens using secure token service
    TokenService.clearTokens();
    
    // Clear other user data
    const keysToRemove = [
      "userInfo",
      // "hospital_name",  // Keep hospital info for branding persistence
      // "hospital_phone",
      "type",
      // "consultation_audio",
      // "consultation_video",
      // "hospital_primary_color",
      // "hospital_secondary_color",
      // "hospital_opaque_color",
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log('localStorage and tokens cleared successfully');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};


const createUserAction = (filenumber) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_USER_REQUEST });

    const data = await UserService.getPatient(filenumber);

    dispatch({
      type: CREATE_USER_SUCCESS,
      payload: data,
    });

    toast.success('Patient information retrieved successfully');
    
  } catch (error) {
    console.error('Create user error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to retrieve patient information';
    const todo = error.response?.data?.errors;
    
    dispatch({
      type: CREATE_USER_FAIL,
      payload: {
        message: errorMessage,
        todo: todo === 'verify_otp' ? 'verify_otp' : null,
      },
    });

    if (todo !== 'verify_otp') {
      toast.error(errorMessage);
    }
  }
};

const getOTp = (file_number, phone_number) => async (dispatch) => {
  try {
    dispatch({ type: GET_OTP_REQUEST });

    const data = await UserService.sendOTP(file_number, phone_number);

    dispatch({
      type: GET_OTP_SUCCESS,
      payload: data,
    });

    toast.success('OTP sent successfully to your phone');
    
  } catch (error) {
    console.error('Get OTP error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send OTP';
    
    dispatch({
      type: GET_OTP_FAIL,
      payload: errorMessage,
    });

    toast.error(errorMessage);
  }
};

const verifyOtp = (file_number, phone_number, otp) => async (dispatch) => {
  try {
    dispatch({ type: VERIFY_OTP });

    const data = await UserService.verifyOTP(file_number, phone_number, otp);

    dispatch({
      type: VERIFY_OTP_SUCCESS,
      payload: data,
    });

    toast.success('OTP verified successfully');
    
  } catch (error) {
    console.error('Verify OTP error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to verify OTP';
    
    dispatch({
      type: VERIFY_OTP_FAIL,
      payload: errorMessage,
    });

    toast.error(errorMessage);
  }
};

const hexToRgb = (hex) => {
  if (!hex) return null;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
};

const applyThemeDirectly = (colors) => {
  if (!colors) return;
  const root = document.documentElement;
  if (colors.primary_color) {
    root.style.setProperty('--color-primary', hexToRgb(colors.primary_color));
  }
  if (colors.secondary_color) {
    root.style.setProperty('--color-secondary', hexToRgb(colors.secondary_color));
  }
  if (colors.opaque_color) {
    root.style.setProperty('--color-opaque', hexToRgb(colors.opaque_color));
  }
};

const loginUserAction = (file_number, password, patient_id=null, qr_code=null) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_USER_REQUEST });

    const data = await UserService.login(file_number, password, patient_id, qr_code);

    // Save user data to localStorage
    pushToLocalStorage(data);

    // Update Redux state with complete user info structure
    const actualData = data.data || data;
    const completeUserInfo = {
      patient: actualData?.patient,
      hospital: actualData?.hospital,
    };

    // Force CSS variables globally BEFORE React navigates
    if (completeUserInfo.hospital) {
      applyThemeDirectly(completeUserInfo.hospital);
    }

    // Update the Redux state with the complete structure
    dispatch({
      type: LOGIN_USER_SUCCESS,
      payload: {
        ...data?.data,
        userInfo: completeUserInfo,
      },
    });
  
    console.log('Login successful:', data);
    
    // Register FCM token after successful login
    try {
      if (fcmService.isSupported && Notification.permission === 'granted') {
        const fcmToken = await fcmService.getToken();
        if (fcmToken) {
          const deviceInfo = {
            device_type: 'web',
            device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser',
            app_version: '1.0.0',
            os_version: navigator.platform
          };
          
          await notificationApi.registerToken(fcmToken, deviceInfo);
          console.log('FCM token registered successfully after login');
        }
      }
    } catch (fcmError) {
      console.warn('Failed to register FCM token after login:', fcmError);
      // Don't fail the login process if FCM registration fails
    }
    
  } catch (error) {
    console.error('Login error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Login failed';

    const todo = error.response?.data?.errors;

    dispatch({
      type: LOGIN_USER_FAIL,
      payload: { 
        data: error?.response,
        message: errorMessage, 
        todo: todo ==='verify_otp'?'verify_otp' : null,
      },
    });

    if (todo !== 'verify_otp') {
      toast.error(errorMessage);
    }
  }
};


const logoutUserAction = () => async (dispatch) => {
  try {
    dispatch({ type: LOGOUT_USER_REQUEST });
    // Reset login state immediately to prevent auto-navigation on next login page visit
    dispatch({ type: LOGIN_USER_RESET });
    
    // Remove FCM token before logout
    try {
      if (fcmService.isSupported) {
        const fcmToken = await fcmService.getToken();
        if (fcmToken) {
          await notificationApi.removeToken(fcmToken);
          console.log('FCM token removed successfully before logout');
        }
      }
    } catch (fcmError) {
      console.warn('Failed to remove FCM token before logout:', fcmError);
      // Don't fail the logout process if FCM removal fails
    }
    
    const data = await UserService.logout();

    dispatch({
      type: LOGOUT_USER_SUCCESS,
      payload: data,
    });

    // Clear localStorage regardless of API response
    removeFromLocalStorage();
    
    // Reset login state to prevent auto-navigation on next login page visit
    dispatch({ type: LOGIN_USER_RESET });
    
    toast.success('Logged out successfully');
    
  } catch (error) {
    console.error('Logout error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Logout failed';
    
    dispatch({
      type: LOGOUT_USER_FAIL,
      payload: { message: errorMessage },
    });

    // Clear localStorage even if logout API fails
    removeFromLocalStorage();
    
    // Reset login state even if logout API fails
    dispatch({ type: LOGIN_USER_RESET });
    
    // Show error toast but still consider logout successful locally
    toast.warning('Logged out locally, but server logout failed');
  }
};

const fetchHospitalPaymentConfigAction = () => async (dispatch, getState) => {
  try {
    dispatch({ type: FETCH_HOSPITAL_PAYMENT_CONFIG_REQUEST });

    const data = await UserService.getHospitalPaymentConfig();

    dispatch({
      type: FETCH_HOSPITAL_PAYMENT_CONFIG_SUCCESS,
      payload: data,
    });

    // Check if we need to update the existing hospital data in Redux
    const currentState = getState();
    const currentHospital = currentState.loginUser?.userInfo?.hospital;
    
    if (currentHospital && data?.data) {
      const newPaymentConfig = data.data;
      const currentPaymentConfig = currentHospital.payment_config;
      
      // Compare payment configs and update if different
      if (JSON.stringify(currentPaymentConfig) !== JSON.stringify(newPaymentConfig)) {
        dispatch({
          type: UPDATE_HOSPITAL_PAYMENT_CONFIG,
          payload: newPaymentConfig,
        });
        
        // Update localStorage as well
        const updatedHospital = { ...currentHospital, payment_config: newPaymentConfig };
        const storageItems = {
          hospital: updatedHospital.hospital_id || updatedHospital.id || currentHospital.hospital_id || currentHospital.id,
        };
        
        Object.entries(storageItems).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            localStorage.setItem(key, JSON.stringify(value));
          }
        });
        
        console.log('Hospital payment config updated in Redux and localStorage');
      }
    }

    return data;
    
  } catch (error) {
    console.error('Fetch hospital payment config error:', error);
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch payment configuration';
    
    dispatch({
      type: FETCH_HOSPITAL_PAYMENT_CONFIG_FAIL,
      payload: errorMessage,
    });

    throw error; // Re-throw to allow caller to handle
  }
};

const fetchConsultationCostsAction = () => async (dispatch, getState) => {
  try {
    dispatch({ type: FETCH_CONSULTATION_COSTS_REQUEST });

    const data = await UserService.getConsultationCosts();

    dispatch({
      type: FETCH_CONSULTATION_COSTS_SUCCESS,
      payload: data,
    });

    // Check if we need to update the existing consultation costs in localStorage
    const currentCosts = {
      audio_consultation: JSON.parse(localStorage.getItem('consultation_audio')) || 2500,
      video_consultation: JSON.parse(localStorage.getItem('consultation_video')) || 5000,
    };

    const newCosts = data?.data;

    // Update localStorage if costs have changed
    if (newCosts && (
      currentCosts.audio_consultation !== newCosts.audio_consultation ||
      currentCosts.video_consultation !== newCosts.video_consultation
    )) {
      localStorage.setItem('consultation_audio', JSON.stringify(newCosts.audio_consultation));
      localStorage.setItem('consultation_video', JSON.stringify(newCosts.video_consultation));
      
      dispatch({
        type: UPDATE_CONSULTATION_COSTS,
        payload: newCosts,
      });
    }

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch consultation costs';
    
    dispatch({
      type: FETCH_CONSULTATION_COSTS_FAIL,
      payload: { message: errorMessage },
    });
  }
};

export { createUserAction, loginUserAction, getOTp, verifyOtp, logoutUserAction, fetchHospitalPaymentConfigAction, fetchConsultationCostsAction };
